import React, { useState } from "react";
import {
  Truck, Settings, Share2, ClipboardList, Command,
  MapPin, Route, Fuel, Clock, Navigation, User,
  FileText, Phone, AlertTriangle,
  ChevronLeft,
  Maximize2
} from "lucide-react";

/**
 * Format thời gian
 */
const formatTime = (unix) => {
  if (!unix) return "--";
  const d = new Date(unix * 1000);
  return d.toLocaleDateString("vi-VN") + " " + d.toLocaleTimeString("vi-VN");
};

const InfoRow = ({ label, value, valueClass = "text-gray-800" }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className={`text-xs font-semibold ${valueClass}`}>{value || "--"}</span>
  </div>
);

const MonitoringPanel = ({ device }) => {
  const [isOpen, setIsOpen] = useState(true);

  // 👉 Khi panel bị ẩn → hiện nút mở
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-6 top-35 z-[1001] 
                  bg-gray-800 text-white 
                  p-1 rounded-lg shadow-lg
                  hover:bg-gray-700
                  transition-all duration-200
                  flex items-center justify-center"
        title="Mở bảng thông tin"
      >
        <Maximize2 size={16} />
      </button>
    );
  }

  // 👉 Chưa chọn thiết bị
  if (!device) {
    return (
      <div className="w-[280px] flex-shrink-0 bg-white border-l border-gray-200 flex items-center justify-center h-full relative">
        {/* Nút đóng */}
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
        >
          ✕
        </button>

        <div className="text-center text-gray-400 p-6">
          <Truck size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Chọn thiết bị</p>
          <p className="text-xs mt-1">để xem thông tin chi tiết</p>
        </div>
      </div>
    );
  }

  const isOnline = device.acc_status === "On";

  return (
    <div className="w-[280px] flex-shrink-0 bg-white border-l border-gray-200 flex flex-col h-full overflow-hidden relative transition-all duration-300">

      {/* HEADER */}
      <div className="px-3 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between mb-1">

          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-700"></div>
            <span className="text-sm font-bold text-gray-800 truncate">
              GT-MDVR02 Plus
            </span>
          </div>

          {/* Nút đóng + tools */}
          <div className="flex items-center gap-1">
            <button className="p-1.5 hover:bg-gray-200 rounded text-gray-500">
              <Navigation size={14} />
            </button>
            <button className="p-1.5 hover:bg-gray-200 rounded text-gray-500">
              <Share2 size={14} />
            </button>
            <button className="p-1.5 hover:bg-gray-200 rounded text-gray-500">
              <ClipboardList size={14} />
            </button>
            <button className="p-1.5 hover:bg-gray-200 rounded text-gray-500">
              <Command size={14} />
            </button>

            {/* Nút đóng */}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-red-100 rounded text-red-500 text-xs"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Status */}
        <div className="bg-black text-white text-[11px] font-semibold px-3 py-1.5 rounded flex items-center gap-2">
          <AlertTriangle size={12} />
          {isOnline ? "Đang hoạt động" : "Mất kết nối"}
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto">

        {/* Quãng đường */}
        <div className="px-3 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 py-1 text-[11px]">
            <Route size={13} className="text-gray-400" />
            <span className="text-gray-500 flex-1">Quãng đường:</span>
            <span className="font-bold text-gray-800">
              {device.mileage || "0"} km
            </span>
          </div>
        </div>

        {/* Thiết bị */}
        <div className="px-3 py-3 border-b border-gray-100">
          <div className="text-[10px] font-bold text-gray-400 mb-2">
            Thông tin thiết bị
          </div>
          <InfoRow label="IMEI" value={device.imei} />
          <InfoRow label="Thời gian" value={formatTime(device.gpsdate_unix)} />
          <InfoRow
            label="Tọa độ"
            value={
              device.latitude
                ? `${device.latitude.toFixed(6)}, ${device.longitude.toFixed(6)}`
                : "--"
            }
            valueClass="text-amber-600"
          />
        </div>

        {/* Trạng thái */}
        <div className="px-3 py-3 border-b border-gray-100">
          <div className="text-[10px] font-bold text-gray-400 mb-2">
            Trạng thái
          </div>
          <InfoRow
            label="ACC"
            value={device.acc_status === "On" ? "BẬT" : "TẮT"}
            valueClass={isOnline ? "text-emerald-600" : "text-gray-400"}
          />
          <InfoRow
            label="Động cơ"
            value={isOnline ? "ĐANG CHẠY" : "DỪNG"}
            valueClass={isOnline ? "text-emerald-600" : "text-gray-400"}
          />
        </div>
      </div>
    </div>
  );
};

export default MonitoringPanel;